import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';

const isSolo = process.env.REACT_APP_SOLO_LANDING === 'true';
const linkCl = isSolo ? 'text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800';
const brand = isSolo ? 'SEN-Education' : 'SEN-Ed';
const summaryBoxCl = isSolo ? 'bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 mb-8' : 'bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6 mb-8';
const summaryTextCl = isSolo ? 'text-purple-800 dark:text-purple-300 mb-0' : 'text-indigo-800 dark:text-indigo-300 mb-0';

const TermsOfService = () => (
  <LegalLayout>
    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Terms of Service</h1>
    <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
      Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
    </p>

    <div className={summaryBoxCl}>
      <p className={summaryTextCl}>
        Please read these terms carefully before using {brand}. By accessing or using our platform,
        you agree to be bound by these terms.
      </p>
    </div>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">1. About These Terms</h2>
      <p className="text-gray-700 dark:text-gray-300">
        These Terms of Service (&quot;Terms&quot;) govern your use of the {brand} platform (&quot;Service&quot;),
        operated by {brand} (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;). By using our Service, you agree to these Terms.
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        If you are using the Service on behalf of a school or organisation, you represent that you have
        the authority to bind that organisation to these Terms.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">2. Description of Service</h2>
      <p className="text-gray-700 dark:text-gray-300">
        {brand} is an educational technology platform providing:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Special Educational Needs (SEN) curriculum resources and teaching activities</li>
        <li>Intervention planning and tracking tools</li>
        <li>SEND provision management for schools</li>
        <li>AI-powered educational content generation</li>
        <li>Assessment and screening tools</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">3. Account Registration</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Eligibility</h3>
      <p className="text-gray-700 dark:text-gray-300">
        You must be at least 18 years old or the age of majority in your jurisdiction to create an account.
        The Service is designed for educators and school staff, not for direct use by students.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Account Security</h3>
      <p className="text-gray-700 dark:text-gray-300">
        You are responsible for:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Maintaining the confidentiality of your login credentials</li>
        <li>All activities that occur under your account</li>
        <li>Notifying us immediately of any unauthorised access</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">4. Subscriptions and Payment</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Individual Subscriptions</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Subscriptions are billed monthly or annually as selected</li>
        <li>Free trials, where offered, convert to paid subscriptions unless cancelled</li>
        <li>Payments are processed securely through Stripe</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">School Subscriptions</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>School pricing is agreed on a per-school basis</li>
        <li>Invoicing terms are specified in individual agreements</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Cancellation</h3>
      <p className="text-gray-700 dark:text-gray-300">
        You may cancel your subscription at any time. Access continues until the end of the current billing period.
        We do not provide refunds for partial subscription periods.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">5. Acceptable Use</h2>
      <p className="text-gray-700 dark:text-gray-300">You agree NOT to:</p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Use the Service for any unlawful purpose</li>
        <li>Share your account credentials with unauthorised users</li>
        <li>Attempt to access accounts or data belonging to others</li>
        <li>Interfere with or disrupt the Service</li>
        <li>Copy, redistribute, or resell content without permission</li>
        <li>Use automated tools to scrape or extract data</li>
        <li>Upload malicious content or code</li>
        <li>Impersonate others or misrepresent your affiliation</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">6. Intellectual Property</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Our Content</h3>
      <p className="text-gray-700 dark:text-gray-300">
        The Service, including all content, features, and functionality, is owned by {brand} and protected
        by copyright, trademark, and other intellectual property laws.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Your Use</h3>
      <p className="text-gray-700 dark:text-gray-300">
        We grant you a limited, non-exclusive, non-transferable licence to use the Service for your
        personal or school&apos;s educational purposes. This licence does not include the right to:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Resell or commercially exploit the Service</li>
        <li>Modify or create derivative works (except resources generated for classroom use)</li>
        <li>Use content in competing products or services</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">User-Generated Content</h3>
      <p className="text-gray-700 dark:text-gray-300">
        Content you create using our tools (such as curriculum plans or resources) belongs to you or your school.
        You grant us a licence to store, display, and process this content to provide the Service.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">7. Third-Party Resources</h2>
      <p className="text-gray-700 dark:text-gray-300">
        Our platform includes links to external resources (such as YouTube videos and product recommendations).
        These are provided for convenience and educational purposes. We do not control or endorse these
        third-party sites and are not responsible for their content or practices.
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        Some links may be affiliate links. See our <Link to="/privacy" className={linkCl}>Privacy Policy</Link> for details.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">8. Data Protection (Schools)</h2>
      <p className="text-gray-700 dark:text-gray-300">
        For school accounts processing student data:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>The school remains the data controller for student data</li>
        <li>{brand} acts as a data processor under UK GDPR</li>
        <li>We will process data only as instructed by the school</li>
        <li>Data Processing Agreements are available on request</li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        See our <Link to="/privacy" className={linkCl}>Privacy Policy</Link> and <Link to="/security" className={linkCl}>Security page</Link> for more details.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">9. Disclaimer</h2>
      <p className="text-gray-700 dark:text-gray-300">
        The Service is provided &quot;as is&quot; without warranties of any kind. While we strive to provide accurate
        and helpful educational content:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Content is for general educational guidance and should be adapted to individual student needs</li>
        <li>AI-generated content should be reviewed by qualified professionals before use</li>
        <li>We do not guarantee specific educational outcomes</li>
        <li>Content does not constitute professional educational or medical advice</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">10. Limitation of Liability</h2>
      <p className="text-gray-700 dark:text-gray-300">
        To the maximum extent permitted by law, {brand} shall not be liable for any indirect, incidental,
        special, consequential, or punitive damages arising from your use of the Service.
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        Our total liability shall not exceed the amount you paid for the Service in the 12 months
        preceding the claim.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">11. Changes to Terms</h2>
      <p className="text-gray-700 dark:text-gray-300">
        We may update these Terms from time to time. We will notify you of material changes via email
        or through the Service. Continued use after changes constitutes acceptance of the updated Terms.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">12. Termination</h2>
      <p className="text-gray-700 dark:text-gray-300">
        We may suspend or terminate your access to the Service if you violate these Terms.
        You may terminate your account at any time by contacting us or through account settings.
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        Upon termination, your right to use the Service ceases immediately. We may retain certain
        data as required by law or for legitimate business purposes.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">13. Governing Law</h2>
      <p className="text-gray-700 dark:text-gray-300">
        These Terms are governed by the laws of England and Wales. Any disputes shall be subject to
        the exclusive jurisdiction of the courts of England and Wales.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">14. Contact</h2>
      <p className="text-gray-700 dark:text-gray-300">
        For questions about these Terms, please contact us at:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Email:</strong> <a href="mailto:support@sen-ed.co.uk" className={linkCl}>support@sen-ed.co.uk</a></li>
      </ul>
    </section>
  </LegalLayout>
);

export default TermsOfService;
